/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import CatalogCorrelationsContext from './CatalogCorrelationsContext';
import initialCatalogCorrelationsState from './initialCatalogCorrelationsState';
import catalogCorrelationsReducer from './reducer/catalogCorrelations.reducer';
import * as action from './reducer/catalogCorrelations.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import useContextUser from 'hooks/contexts/useContextUser';
import { IGenericType } from 'interfaces/generics.interface';
import { TValuesToAssign } from 'interfaces/catalogCorrelations.interface';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextUILanguages from 'hooks/contexts/useContextUILanguages';
import useContextCompany from 'hooks/contexts/useContextCompany';

function CatalogCorrelationsProvider(props: IChildrenProps) {
  const [catalogCorrelationsState, dispatch] = useReducer(
    catalogCorrelationsReducer,
    initialCatalogCorrelationsState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();
  const { languages } = useContextUILanguages();

  const getCatalogCorrelations = useCallback(
    async ({ type }: { type: IGenericType }) => {
      if (!company?.brand?.id || !dbUser?.i18n_lang_id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const languageCode = languages.find(
        (lang) => lang.id === dbUser.i18n_lang_id
      )?.code;
      if (!languageCode) return false;
      return await action.getGenericsAction({
        dispatch,
        brandId: company.brand.id,
        languageCode,
        type,
        token,
        translate,
      });
    },
    [company?.brand?.id, dbUser?.i18n_lang_id]
  );

  const assignGenerics = useCallback(
    async ({ type, values }: { type: IGenericType; values: TValuesToAssign }) => {
      if (!company?.brand?.id || !dbUser?.i18n_lang_id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const languageCode = languages.find(
        (lang) => lang.id === dbUser.i18n_lang_id
      )?.code;
      if (!languageCode) return false;
      return await action.assignGenericAction({
        dispatch,
        brandId: company.brand.id,
        languageCode,
        type,
        values,
        token,
        translate,
      });
    },
    [company?.brand?.id, dbUser?.i18n_lang_id]
  );

  const memoProvider = useMemo(
    () => ({
      ...catalogCorrelationsState,
      assignGenerics,
      getCatalogCorrelations,
    }),
    [catalogCorrelationsState, assignGenerics, getCatalogCorrelations]
  );

  return (
    <CatalogCorrelationsContext.Provider value={memoProvider}>
      {props.children}
    </CatalogCorrelationsContext.Provider>
  );
}

export default memo(CatalogCorrelationsProvider);
